<template>
    <div :class="['user-row', { 'is-inactive': status === USER_STATUS.INACTIVE, 'is-owner': isOwner, 'is-context': isContext }]">
        <div class="user-row__identity">
            <div class="user-row__email-line">
                <span class="user-row__email" :title="user.email">{{ user.email }}</span>
                <span v-if="isSelf" class="user-row__self">Вы</span>
                <span class="user-row__id">#{{ user.id }}</span>
            </div>
            <div class="user-row__badges">
                <UserRoleBadge :role-code="user.role?.code" :role-name="user.role?.name" />
                <span v-if="status === USER_STATUS.PENDING" class="status-badge status-badge--pending">Ожидает одобрения</span>
                <span v-else-if="status === USER_STATUS.INACTIVE" class="status-badge status-badge--inactive">Неактивен</span>
                <span v-if="user.access_mode === ACCESS_MODE.READONLY" class="status-badge status-badge--readonly">Только просмотр</span>
            </div>
        </div>

        <div class="user-row__scope">
            <span v-if="isAdmin" class="user-row__muted">Доступ ко всем данным</span>
            <span v-else-if="status === USER_STATUS.INACTIVE" class="user-row__muted">Доступы сняты при деактивации</span>
            <UserScopeChips
                v-else-if="scopeKind"
                :ids="scopeIds"
                :names="scopeKind === 'partner' ? partnerNames : advertiserNames"
                :kind="scopeKind"
            />
        </div>

        <div class="user-row__actions">
            <span v-if="busy" class="user-row__busy" role="status">Сохранение…</span>
            <template v-else>
                <button
                    v-if="primaryAction"
                    type="button"
                    class="user-row__primary"
                    @click="emit('action', primaryAction.id, user)"
                >
                    {{ primaryAction.label }}
                </button>
                <ActionsMenu
                    v-if="menuItems.length"
                    :items="menuItems"
                    @select="emit('action', $event, user)"
                />
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import UserRoleBadge from '../UserRoleBadge.vue';
import UserScopeChips from './UserScopeChips.vue';
import ActionsMenu from '../../ui/ActionsMenu.vue';
import { ROLE, isClientRole } from '../../../constants/roles';
import { ACCESS_MODE, USER_ACTION, USER_STATUS, userStatusOf } from '../../../constants/users';

const props = defineProps({
    user: { type: Object, required: true },
    actorId: { type: Number, required: true },
    partnerNames: { type: Map, required: true },
    advertiserNames: { type: Map, required: true },
    isOwner: { type: Boolean, default: false },
    isContext: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
});

const emit = defineEmits(['action']);

const status = computed(() => userStatusOf(props.user));
const isSelf = computed(() => props.user.id === props.actorId);
const isAdmin = computed(() => props.user.role?.code === ROLE.ADMIN);

const scopeKind = computed(() => {
    if (props.user.role?.code === ROLE.AGENCY) return 'partner';
    if (isClientRole(props.user.role?.code)) return 'advertiser';
    return null;
});

const scopeIds = computed(() => (scopeKind.value === 'partner'
    ? props.user.scope?.partner_ids
    : props.user.scope?.advertiser_ids) || []);

const abilities = computed(() => props.user.abilities || {});

/** Главное действие выносится кнопкой: одобрение заявки или восстановление важнее редактирования. */
const primaryAction = computed(() => {
    if (abilities.value.approve) return { id: USER_ACTION.APPROVE, label: 'Одобрить' };
    if (abilities.value.restore) return { id: USER_ACTION.RESTORE, label: 'Восстановить' };
    return null;
});

const menuItems = computed(() => [
    abilities.value.update && { id: USER_ACTION.EDIT, label: 'Редактировать' },
    abilities.value.reset_password && { id: USER_ACTION.RESET_PASSWORD, label: 'Сбросить пароль' },
    abilities.value.deactivate && { id: USER_ACTION.DEACTIVATE, label: 'Деактивировать', danger: true },
].filter(Boolean));
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.user-row {
    display: grid;
    grid-template-columns: minmax(220px, 2fr) minmax(0, 3fr) auto;
    align-items: center;
    gap: $spacing-lg;
    padding: $spacing-md $spacing-lg;
    border-top: 1px solid $admin-border-color;
    background: $admin-bg-primary;

    &:hover { background: $admin-bg-secondary; }

    &.is-owner {
        border-top: none;
        background: transparent;

        .user-row__email { font-weight: 600; font-size: $font-size-md; }
    }

    &.is-context { opacity: 0.7; }

    &.is-inactive {
        .user-row__email { color: $admin-text-muted; text-decoration: line-through; }
    }

    @media (max-width: $breakpoint-md) {
        grid-template-columns: 1fr;
        gap: $spacing-sm;
    }
}

.user-row__identity {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
    min-width: 0;
}

.user-row__email-line {
    display: flex;
    align-items: baseline;
    gap: $spacing-sm;
    min-width: 0;
}

.user-row__email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    color: $admin-text-primary;
}

.user-row__self {
    flex-shrink: 0;
    padding: 0 $spacing-xs;
    border-radius: $radius-sm;
    background: #e0e7ff;
    color: #3730a3;
    font-size: $font-size-xs;
    font-weight: 600;
}

.user-row__id {
    flex-shrink: 0;
    font-size: $font-size-xs;
    color: $admin-text-light;
}

.user-row__badges {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-xs;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem $spacing-sm;
    border-radius: $radius-sm;
    font-size: 0.6875rem;
    font-weight: 500;
    white-space: nowrap;

    &--pending { background: $admin-warning-bg; color: $admin-warning-color; }
    &--inactive { background: $admin-bg-hover; color: $admin-text-muted; }
    &--readonly { background: $admin-bg-hover; color: $admin-text-secondary; }
}

.user-row__scope {
    min-width: 0;
}

.user-row__muted {
    font-size: $font-size-sm;
    color: $admin-text-light;
}

.user-row__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $spacing-sm;

    @media (max-width: $breakpoint-md) {
        justify-content: flex-start;
    }
}

.user-row__primary {
    padding: 0.35rem 0.8rem;
    border: none;
    border-radius: $radius-md;
    background: $admin-primary-color;
    color: #fff;
    font-size: $font-size-sm;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &:hover { background: $admin-primary-hover; }
}

.user-row__busy {
    font-size: $font-size-sm;
    color: $admin-text-muted;
}
</style>
